import React, { useEffect, useState } from 'react';
import { Box, TextField, IconButton, Tooltip, useTheme, Typography, Button } from '@mui/material';
import i18n from 'translation';
import APIRequest, { APIError } from 'system/APIRequest';
import SavedQueryRow from 'components/queries/savedQueries/SavedQueryRow';
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import { Query, defaultQuery, queryOps } from 'system/query/Query';
import QueryDialog from 'components/queries/QueryDialog';
import { AutoAwesome, Check, DeleteForever, Edit, PlayCircleOutlined } from '@mui/icons-material';
import { optimizeQuery } from 'system/query/QueryUtility';
import { QueryFieldsSelector } from 'components/queries/queryBuilder/dialogParts/QueryFieldsSelector';
import { TemplateQueryProps } from './TemplateSearchContent';
import AIGradientTextField from 'components/design/AI/AIGradientTextField';
import { enqueueSnackbar } from 'notistack';
import { currentUser, permissionTest, UserPreference } from 'system/InfegyUser';
import AIInlineLoader from 'components/design/AI/AIInlineLoader';
import { QueryInfo } from 'system/query/QueryInfo';
import BooleanQueryField from 'components/queries/BooleanQueryField';

interface QuickSearchProps {
  queryInfo: any;

  index?: number;

  datasetId: string;

  templateQuery: TemplateQueryProps;
  setTemplateQuery: (query: TemplateQueryProps) => void;

  canDelete?: boolean;
  onDelete?: Function;

  isLoading: boolean | undefined;
  setIsLoading: (isLoading: boolean) => void;

  onSubmit?: () => void;
}

export function findTemplateSearchFields(queryInfo: QueryInfo, fieldIds: string[]): any | undefined {
  const tempFieldIds = fieldIds.filter(fieldId => !!fieldId),
        textFields = queryInfo?.fields?.filter(field => field.type === "text") || [],
        validFieldIds = textFields.map(field => field.id),
        validCurrentFields = tempFieldIds.filter(fieldId => validFieldIds.includes(fieldId));

  if (validCurrentFields.length !== tempFieldIds.length) {
    return {
      fields: validCurrentFields,
      usingDefaults: true
    }
  }

  // If the user has selected fields, don't try to use defaults.
  if (tempFieldIds.length || !queryInfo) {
    return;
  }

  // If the user has not selected any fields,
  // or if the fields they selected are not valid, try to use defaults
  const idealDefaultFields = ["body", "title", "description"];

  // Try to use the ideal text fields if they exist
  let defaultFieldIds:string[] = textFields.filter(field => {
    return idealDefaultFields.includes(field.id);
  }).map(field => field.id);

  // Couldn't find the ideal fields, so just use the first text field
  if (!defaultFieldIds.length && textFields.length) {
    defaultFieldIds = [textFields[0]?.id];
  }

  if (!tempFieldIds.length && defaultFieldIds.length) {
    return {
      fields: defaultFieldIds,
      usingDefaults: false
    }
  }
}

export default function QuickSearch(props: QuickSearchProps): JSX.Element {
  const templateQueriesContext = useTemplateQueries();
  const theme = useTheme();
  const user = currentUser();

  const { templateQuery, isLoading, setIsLoading } = props;
  const usingAI = templateQuery.usingAIQueryBuilder;

  const [query, setQuery] = useState<any>(undefined);
  const [queryDialogIsOpen, setQueryDialogIsOpen] = useState<boolean>(false);
  const [isEditingFields, setIsEditingFields] = useState<boolean>(false);

  const defaultLabel = `${i18n.t('widget-types.template-search.query-title-placeholder')} ${(props.index || 0) + 1}`;

  useEffect(() => {
    // Check if the user has permission for AI, if not, disable the AI button
    if (!permissionTest("ai_features") && templateQuery.usingAIQueryBuilder) {
      const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
      templateQueryCopy.usingAIQueryBuilder = false;
      props.setTemplateQuery(templateQueryCopy);
    }
  }, [templateQuery.usingAIQueryBuilder]);

  useEffect(() => {
    if (templateQuery.savedQueryId) {
      APIRequest(`obj/query/${templateQuery.savedQueryId}`).then((response: any) => {
        const query = response.output as Query;
        setQuery(query);
      }).catch((error: APIError) => {
        // Display the error unless it's due to a deleted query
        if (error.code !== 404) {
          enqueueSnackbar(`${error.message}`, {variant: 'error', persist: true});
        }
        // If the saved query is not found, convert to an empty template query
        const newTemplateQuery = new TemplateQueryProps(user);
        props.setTemplateQuery(newTemplateQuery);
      });
    }
  }, [templateQuery.savedQueryId]);

  // Setup the default fields to use for the quick search queries.
  useEffect(() => {
    if (!props.queryInfo) {
      return;
    }
    if (!templateQuery.searchFieldIds?.length && !usingAI && !isEditingFields) {
      const searchFields = findTemplateSearchFields(props.queryInfo, templateQuery.searchFieldIds || []);
      if (searchFields && searchFields.fields?.length) {
        const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
        templateQueryCopy.searchFieldIds = searchFields.fields;
        props.setTemplateQuery(templateQueryCopy);
      }
    }
  }, [props.queryInfo, templateQuery.searchFieldIds, usingAI]);

  // set to a copy of the query so that we can edit it without changing the original
  let queryDialogQuery = new Query({...query});

  return (
    <Box sx={{ p:2, pt: 0, pb: 1, position: "relative" }}>
      <Box>
        {templateQuery.savedQueryId && query &&
          <Box sx={{ pt: 1}}>
            <SavedQueryRow
              savedQuery={query}
              queryInfo={templateQueriesContext.queryInfo}
              loadOwnQueryInfo={true}
              showEditButton={true}
              showDeleteButton={true}
              canDelete={true}
              canEdit={true}
              onChange={(queryId) => {
                const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                templateQueryCopy.savedQueryId = queryId;
                props.setTemplateQuery(templateQueryCopy);

                APIRequest(`obj/query/${queryId}`).then((response: any) => {
                  setQuery(response.output as Query);
                });
              }}
              onDelete={() => {
                if (props.onDelete) {
                  props.onDelete();
                }
              }}
            />
          </Box>
        }
        {!templateQuery.savedQueryId &&
          <>
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "auto auto auto"}, alignItems: "center", mb: 1, mr: "40px"}}>
              <Box sx={{ pt: 0.5, whiteSpace: "nowrap" }}>
                {!usingAI &&
                  <>
                    <Typography
                      variant="h4" color="textSecondary"
                      sx={{ verticalAlign:'bottom', p:0, pb: 0, pt: 1, pr: 1, display: 'inline-block' }}>
                      {i18n.t("title")}:
                    </Typography>
                    <TextField
                      autoComplete="off"
                      variant="standard"
                      placeholder={defaultLabel}
                      sx={{ p: 0, pb:0, maxWidth: "250px" }}
                      value={templateQuery.queryTitle || ""}
                      onChange={(e) => {
                        const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                        templateQueryCopy.queryTitle = e.target.value;
                        props.setTemplateQuery(templateQueryCopy);
                      }} />
                  </>
                }
                {usingAI &&
                  <Typography variant="body1" sx={{ ml: 0, pt: 1}}>
                    {i18n.t('widget-types.template-search.ai-description')}
                  </Typography>
                }
              </Box>
              <Box sx={{textAlign: {xs:"left", md: "center"}, pt: 1.5}}>
                {(props.queryInfo && !usingAI && isEditingFields) &&
                  <>
                    <Typography
                      variant="h4" color="textSecondary"
                      sx={{ verticalAlign:'bottom', p: 0, pt: 1, pr: 1, display: 'inline-block' }}>
                      {i18n.t("widget-types.template-search.search-fields")}:
                    </Typography>
                    <QueryFieldsSelector
                      variant='standard'
                      size='small'
                      isInline={true}
                      queryInfo={props.queryInfo}
                      allowedFieldTypes={["text"]}
                      selectedFieldIds={templateQuery.searchFieldIds}
                      setSelectedFieldIds={(value: string[]) => {
                        const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                        templateQueryCopy.searchFieldIds = value;
                        props.setTemplateQuery(templateQueryCopy);
                      }} />
                    <Check
                      color='success'
                      sx={{ opacity: 0.6, "&:hover": { opacity: 1 }, ml: 0.5, fontSize: "1.5rem", verticalAlign: "middle", cursor: "pointer" }}
                      onClick={() => {
                        setIsEditingFields(!isEditingFields);
                      }} />
                  </>
                }
                {!isEditingFields && !usingAI &&
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    onClick={() => {
                      setIsEditingFields(!isEditingFields);
                    }}

                    sx={{ verticalAlign:'bottom', p: 0, pt: 0, pr: 1, display: 'inline-block', opacity: 0.7, "&:hover": { opacity: 1 }, cursor: "pointer" }}>
                    {i18n.t("widget-types.template-search.search-fields")}: &nbsp;
                    {templateQuery.searchFieldIds?.length &&
                      templateQuery.searchFieldIds.map((fieldId: string) => {
                        const field = props.queryInfo.fields.find((f: any) => f.id === fieldId);
                        return field ? field.label : "";
                      }).join(", ")
                    }
                    <Edit
                      sx={{
                        opacity: 0.6,
                        "&:hover": { opacity: 1 },
                        ml: 0.5,
                        fontSize: "1rem",
                        verticalAlign: "middle",
                        cursor: "pointer"
                      }}
                    />
                  </Typography>
                }
              </Box>
              <Box sx={{ textAlign: {xs: "left", md: "right"}, pt: 1 }}>
                 <Tooltip title={i18n.t('widget-types.template-search.toggle-ai')} followCursor={true}><span>
                    <Button
                      variant='text'
                      size="small"
                      disabled={!permissionTest("ai_features") && !usingAI}
                      onClick={() => {
                        const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                        templateQueryCopy.usingAIQueryBuilder = !templateQuery.usingAIQueryBuilder;
                        props.setTemplateQuery(templateQueryCopy);
                      }}
                      startIcon={usingAI ? <AutoAwesome /> : undefined}
                      >
                      {usingAI ? i18n.t('widget-types.template-search.ai-query-assist-on') : i18n.t('widget-types.template-search.ai-query-assist-off')}
                    </Button>
                </span></Tooltip>
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 40px", alignItems: "center"}}>
            {usingAI && (
            <AIGradientTextField
              hideGradient={!usingAI}
              value={templateQuery.quickSearchContent}
              onSubmit={props.onSubmit}
              submitOnEnter={usingAI}
              onChange={(e) => {
                const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                templateQueryCopy.quickSearchContent = e.target.value;
                props.setTemplateQuery(templateQueryCopy);
              }}
              placeholder={usingAI ? i18n.t('ai-query.ai-query-prompt-placeholder') : i18n.t('widget-types.template-search.query-placeholder')} />
            )}
            {!usingAI && (
              <BooleanQueryField
                height={30}
                content={templateQuery.quickSearchContent}
                setContent={(content: string) => {
                  if (content !== templateQuery.quickSearchContent) {
                    const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                    templateQueryCopy.quickSearchContent = content;
                    props.setTemplateQuery(templateQueryCopy);
                  }
                }}
                placeholder={i18n.t('widget-types.template-search.query-placeholder')} />
            )}

            <Box>

            {!usingAI &&
                <Tooltip title={i18n.t('widget-types.template-search.edit-query-in-builder')} followCursor={true}><span>
                  <IconButton
                    sx={{
                      ml: 1,
                      opacity: 0.6,
                      "&:hover": { opacity: 1, color: theme.colors.namedColors.chart1 },
                      cursor: "pointer"
                    }}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      event.preventDefault();
                      const content = templateQuery.quickSearchContent.trim();
                      if (!content.length) {
                        const blankQuery = new Query({
                          name: templateQuery.queryTitle,
                          dataset_id: props.datasetId,
                          query: {
                            op: queryOps.and,
                            values: []
                          }
                        });
                        setQuery(blankQuery);
                        setQueryDialogIsOpen(true);
                      } else {
                        const newQuery = new Query({
                          name: templateQuery.queryTitle,
                          dataset_id: props.datasetId,
                          query: {
                            op: queryOps.boolean,
                            fields: templateQuery.searchFieldIds,
                            value: content
                          }
                        });
                        if (user?.getPreference(UserPreference.SKIP_CONVERTING_QUICKSEARCH_BOOLEAN) === "on") {
                          queryDialogQuery = {...newQuery};
                          newQuery.query = {
                            op: queryOps.and,
                            values: [ newQuery.query ]
                          };
                          setQuery(newQuery);
                          setQueryDialogIsOpen(true);
                        } else {
                          optimizeQuery(newQuery).then((optimizedQuery) => {
                            optimizedQuery.name = templateQuery.queryTitle;
                            queryDialogQuery = optimizedQuery;
                            setQuery(optimizedQuery);
                            setQueryDialogIsOpen(true);
                          });
                        }
                      }
                    }}>
                    <Edit />
                  </IconButton>
                </span></Tooltip>
              }
              {usingAI &&
                <Tooltip title={i18n.t('widget-types.template-search.generate-ai-query')} followCursor={true}><span>
                  <IconButton
                    disabled={!templateQuery.quickSearchContent.length}
                    sx={{
                      ml: 1,
                      opacity: 0.6,
                      "&:hover": { opacity: 1, color: theme.colors.namedColors.chart1 },
                      cursor: "pointer"
                    }}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setIsLoading(true);
                      const content = templateQuery.quickSearchContent.trim();
                      if (content.length) {
                        APIRequest(`query/ai-assist`, {
                          prompt: content,
                        }).then((response: any) => {
                          const newQuery = new Query({
                            dataset_id: props.datasetId,
                            query: response,
                            prompt: content,
                            name: content
                          });
                          APIRequest("obj/query", {}, {
                            method: "POST",
                            body: JSON.stringify(newQuery, undefined, 2),
                          }).then((savedResponse) => {
                            const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
                            templateQueryCopy.aiQueryBuilderCompletedPrompt = content as string;
                            templateQueryCopy.aiQueryBuilderQuery = savedResponse as Query;
                            templateQueryCopy.savedQueryId = (savedResponse as Query).id;
                            props.setTemplateQuery(templateQueryCopy);
                            setQuery(savedResponse as Query);
                            setIsLoading(false);
                          }).catch((error: APIError) => {
                            setIsLoading(false);
                            console.error("Error saving query", error);
                            enqueueSnackbar(`${error.message}`, {variant: 'error', persist: true});
                          });
                        }).catch((error: APIError) => {
                          setIsLoading(false);
                          enqueueSnackbar(error.message, { variant: "error" });
                        });
                      }
                    }}>
                    <PlayCircleOutlined />
                  </IconButton>
                </span></Tooltip>
              }
            </Box>
            </Box>
            <Box sx={{ position: "absolute", top: "4px", right: "10px", zIndex: 999 }}>
              {props.canDelete &&
                <Tooltip title={i18n.t('delete')} followCursor={true}><span>
                  <IconButton
                    sx={{
                      opacity: 0.6,
                      "&:hover": { opacity: 1 }
                    }}
                    color='error'
                    disabled={!props.canDelete}
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (props.onDelete) {
                        props.onDelete();
                      }
                    }}>
                    <DeleteForever />
                  </IconButton>
                </span></Tooltip>
              }

            </Box>
          </>
        }
      </Box>
      {queryDialogIsOpen &&
        <QueryDialog
          isOpen={queryDialogIsOpen}
          query={queryDialogQuery || new Query({...defaultQuery, datasetId: props.datasetId})}
          fixedDatasetId={queryDialogQuery.dataset_id || props.datasetId}
          onClose={() => {
            // Reset the query dialog query when the dialog is closed
            queryDialogQuery = new Query({...query});
            setQueryDialogIsOpen(false);
          }}
          onSubmit={(queryId:string) => {
            const templateQueryCopy = {...templateQuery} as TemplateQueryProps;
            templateQueryCopy.savedQueryId = queryId;
            props.setTemplateQuery(templateQueryCopy);

            APIRequest(`obj/query/${queryId}`).then((response: any) => {
              setQuery(response.output as Query);
            });
            setQueryDialogIsOpen(false);
          }} />
      }
      {isLoading &&
        <>
        <Box sx={{ position: "absolute", top: "0px", left: "0px", right: "0px", bottom: "0px", backgroundColor: theme.palette.background.paper, opacity: 0.5}} />
        <Box sx={{ position: "absolute", top: "0px", left: "0px", right: "0px", bottom: "0px", backdropFilter: "blur(3px)", textAlign: "center", alignContent: "center", zIndex: 999 }}>
          <Box sx={{}}>
          <AIInlineLoader />
          </Box>
        </Box>
        </>
      }
    </Box>
  );
}